import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Clock, KeyRound, Save, User, Moon } from "lucide-react";
import { toast } from "sonner";
import { getBrowserTimezone, timezoneOptions } from "@/lib/timezone";

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [timezone, setTimezone] = useState("UTC");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingTimezone, setSavingTimezone] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/settings");
        setName(data.name || user?.name || "");
        setCompany(data.company || user?.company || "");
        setEmail(data.email || user?.email || "");
        setTimezone(data.timezone || user?.timezone || getBrowserTimezone());
      } catch {
        setName(user?.name || "");
        setCompany(user?.company || "");
        setEmail(user?.email || "");
        setTimezone(user?.timezone || getBrowserTimezone());
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const saveProfile = async () => {
    if (!name.trim()) return toast.error("Name is required");
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/users/settings", { name: name.trim(), company: company.trim() });
      if (refreshUser) await refreshUser(data.user);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const saveTimezone = async () => {
    setSavingTimezone(true);
    try {
      const { data } = await api.patch("/users/settings", { timezone });
      if (refreshUser) await refreshUser(data.user);
      toast.success("Timezone saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save timezone");
    } finally {
      setSavingTimezone(false);
    }
  };

  const changePassword = async () => {
    if (!currentPassword) return toast.error("Enter your current password");
    if (newPassword.length < 6) return toast.error("New password must be at least 6 characters");
    if (newPassword !== confirmPassword) return toast.error("New passwords do not match");
    setChangingPassword(true);
    try {
      await api.post("/users/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password changed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const tzChoices = timezoneOptions(timezone);

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <PageHeader
        title="Account settings"
        subtitle="Update your profile, password, and timezone preferences."
      />

      {loading ? (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Loading…</p>
      ) : (
        <div className="space-y-8 mt-8">
          <section className="border border-neutral-200 dark:border-neutral-800 rounded-sm p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Moon className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
              <h2 className="font-display font-bold text-xl">Appearance</h2>
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
              Choose light, dark, or match your system preference.
            </p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Theme</Label>
              <Select value={theme} onValueChange={setTheme}>
                <SelectTrigger className="mt-2 rounded-sm" data-testid="settings-theme">
                  <SelectValue placeholder="Select theme" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </section>

          <section className="border border-neutral-200 dark:border-neutral-800 rounded-sm p-6 space-y-4">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
              <h2 className="font-display font-bold text-xl">Profile</h2>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 rounded-sm"
                placeholder="Your name"
                data-testid="settings-name"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Company</Label>
              <Input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="mt-2 rounded-sm"
                placeholder="Company name (optional)"
                data-testid="settings-company"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Email</Label>
              <Input value={email} disabled className="mt-2 rounded-sm bg-neutral-50 dark:bg-neutral-900" data-testid="settings-email" />
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">Contact an administrator to change your email address.</p>
            </div>
            <Button
              onClick={saveProfile}
              disabled={savingProfile}
              className="rounded-sm btn-brand font-semibold"
              data-testid="settings-save-profile"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingProfile ? "Saving…" : "Save profile"}
            </Button>
          </section>

          <section className="border border-neutral-200 dark:border-neutral-800 rounded-sm p-6 space-y-4">
            <div className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
              <h2 className="font-display font-bold text-xl">Password</h2>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Current password</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="mt-2 rounded-sm"
                autoComplete="current-password"
                data-testid="settings-current-password"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">New password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-2 rounded-sm"
                minLength={6}
                autoComplete="new-password"
                data-testid="settings-new-password"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Confirm new password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-2 rounded-sm"
                minLength={6}
                autoComplete="new-password"
                data-testid="settings-confirm-password"
              />
            </div>
            <Button
              onClick={changePassword}
              disabled={changingPassword}
              variant="outline"
              className="rounded-sm font-semibold"
              data-testid="settings-change-password"
            >
              {changingPassword ? "Updating…" : "Change password"}
            </Button>
          </section>

          <section className="border border-neutral-200 dark:border-neutral-800 rounded-sm p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
            <h2 className="font-display font-bold text-xl">Timezone</h2>
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
              Campaigns are stored in UTC and sent at the correct moment for your timezone.
              Tracking domain setup is under Domains.
            </p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Your timezone</Label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger className="mt-2 rounded-sm" data-testid="settings-timezone">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {tzChoices.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
                Browser detected: {getBrowserTimezone()}
              </p>
            </div>
            <Button
              onClick={saveTimezone}
              disabled={savingTimezone}
              className="rounded-sm btn-brand font-semibold"
              data-testid="settings-save-timezone"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingTimezone ? "Saving…" : "Save timezone"}
            </Button>
          </section>
        </div>
      )}
    </div>
  );
}
